import { useCallback, useEffect, useState } from 'react'
import { Badge, Button, EmptyState, Skeleton } from '../../../shared/ui'
import {
  fetchOutreachSettings,
  fetchProgramStatus,
  listPrograms,
  listSequences,
  listSites,
  setProgramActive,
} from '../../../lib/outreach/api'
import { pausedLabel } from '../../../lib/outreach/stats'
import type { Program, ProgramStatus, Sequence, SiteRef } from '../../../lib/outreach/types'
import { toast } from '../../../lib/toast'
import { EnrollDialog } from '../../shared/EnrollDialog'
import { ErrorLine, errorMessage, reportError } from './shared'
import { ProgramForm } from './ProgramForm'

interface Props {
  readOnly: boolean
  userId: string
}

type Editing = Program | 'new' | null

function statusTone(s: ProgramStatus | null | undefined): 'ok' | 'warn' | 'err' | 'muted' {
  if (!s) return 'muted'
  if (!s.paused_reason) return 'ok'
  if (s.paused_reason === 'bounce_stop') return 'err'
  if (s.paused_reason === 'cap_reached') return 'warn'
  return 'muted'
}

function KillSwitch({
  program,
  onChange,
}: {
  program: Program
  onChange: (active: boolean) => void
}) {
  const [asking, setAsking] = useState(false)
  if (asking) {
    return (
      <span className="os-confirm" role="group" aria-label={`Switch on ${program.name}`}>
        <span className="settings-hint os-confirm-note">This program will start sending mail.</span>
        <Button
          onClick={() => {
            setAsking(false)
            onChange(true)
          }}
        >
          Yes, switch on
        </Button>
        <Button variant="secondary" onClick={() => setAsking(false)}>
          Cancel
        </Button>
      </span>
    )
  }
  return (
    <button
      type="button"
      role="switch"
      className="os-switch"
      aria-checked={program.active}
      aria-label={`Sending for ${program.name}`}
      onClick={() => (program.active ? onChange(false) : setAsking(true))}
    >
      <span className="os-switch-track" aria-hidden="true" />
      {program.active ? 'On' : 'Off'}
    </button>
  )
}

export function ProgramsTab({ readOnly, userId }: Props) {
  const [programs, setPrograms] = useState<Program[]>([])
  const [sites, setSites] = useState<SiteRef[]>([])
  const [sequences, setSequences] = useState<Sequence[]>([])
  const [timezone, setTimezone] = useState('UTC')
  const [statuses, setStatuses] = useState<Record<string, ProgramStatus | null>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState<Editing>(null)
  const [enrolling, setEnrolling] = useState<Program | null>(null)

  const loadStatus = useCallback(async (id: string) => {
    try {
      const s = await fetchProgramStatus(id)
      setStatuses((prev) => ({ ...prev, [id]: s }))
    } catch {
      setStatuses((prev) => ({ ...prev, [id]: null }))
    }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [progs, siteRows, seqs, settings] = await Promise.all([
        listPrograms(),
        listSites(),
        listSequences(),
        fetchOutreachSettings(),
      ])
      setPrograms(progs)
      setSites(siteRows)
      setSequences(seqs)
      setTimezone(settings.timezone)
      setLoading(false)
      await Promise.all(progs.map((p) => loadStatus(p.id)))
    } catch (e) {
      setError(errorMessage(e))
      setLoading(false)
    }
  }, [loadStatus])

  useEffect(() => {
    void load()
  }, [load])

  const toggle = async (p: Program, active: boolean) => {
    try {
      await setProgramActive(p.id, active)
      setPrograms((prev) => prev.map((x) => (x.id === p.id ? { ...x, active } : x)))
      toast(active ? `${p.name} is on` : `${p.name} is off`, 'success')
      await loadStatus(p.id)
    } catch (e) {
      setError(reportError(e))
    }
  }

  if (editing) {
    return (
      <ProgramForm
        program={editing === 'new' ? null : editing}
        sites={sites}
        sequences={sequences}
        timezone={timezone}
        onCancel={() => setEditing(null)}
        onSaved={(saved) => {
          setEditing(null)
          setPrograms((prev) =>
            prev.some((x) => x.id === saved.id)
              ? prev.map((x) => (x.id === saved.id ? saved : x))
              : [...prev, saved],
          )
          void loadStatus(saved.id)
        }}
        onDeleted={(id) => {
          setEditing(null)
          setPrograms((prev) => prev.filter((x) => x.id !== id))
        }}
      />
    )
  }

  const siteName = (id: string | null) => sites.find((s) => s.id === id)?.name ?? 'No site'
  const seqName = (id: string | null) => sequences.find((s) => s.id === id)?.name ?? 'No sequence'

  return (
    <div className="os-root">
      <div className="os-bar">
        <span className="settings-hint">
          A program is a send plan: a sequence, a daily cap, send days and safety stops.
        </span>
        {!readOnly && (
          <span className="os-bar-end">
            <Button onClick={() => setEditing('new')}>New program</Button>
          </span>
        )}
      </div>
      <ErrorLine>{error}</ErrorLine>

      {loading ? (
        <div className="os-card" aria-busy="true">
          <Skeleton width="40" />
          <Skeleton width="70" />
        </div>
      ) : programs.length === 0 ? (
        <EmptyState
          title="No programs yet"
          message="Create a program to choose who gets which emails, and how many a day."
          {...(readOnly ? {} : { cta: { label: 'New program', onClick: () => setEditing('new') } })}
        />
      ) : (
        <div className="os-list">
          {programs.map((p) => {
            const s = statuses[p.id]
            return (
              <div className="os-card" key={p.id} data-testid={`program-${p.id}`}>
                <div className="os-card-head">
                  <span className="os-name">{p.name}</span>
                  <Badge>{p.kind}</Badge>
                  <Badge tone={statusTone(s)}>
                    {s ? pausedLabel(s.paused_reason) : 'No status'}
                  </Badge>
                </div>
                <div className="os-meta">
                  <span>Site: {siteName(p.site_id)}</span>
                  <span>Sequence: {seqName(p.sequence_id)}</span>
                  <span>
                    {s
                      ? `Today: ${s.sent_today} sent of ${s.cap_today}, ${s.remaining} remaining`
                      : `Daily cap: ${p.daily_cap}`}
                  </span>
                </div>
                <div className="os-actions">
                  {readOnly ? (
                    <Badge tone={p.active ? 'ok' : 'muted'}>{p.active ? 'On' : 'Off'}</Badge>
                  ) : (
                    <KillSwitch program={p} onChange={(a) => void toggle(p, a)} />
                  )}
                  <Button variant="secondary" disabled={!userId} onClick={() => setEnrolling(p)}>
                    Enroll contacts
                  </Button>
                  {!readOnly && (
                    <Button variant="secondary" onClick={() => setEditing(p)}>
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {enrolling && (
        <EnrollDialog
          programs={programs}
          defaultProgramId={enrolling.id}
          source={{ kind: 'view' }}
          userId={userId}
          onClose={() => setEnrolling(null)}
          onDone={() => void loadStatus(enrolling.id)}
        />
      )}
    </div>
  )
}
